<script setup lang="ts">
import {computed, ref, watch} from "vue";
import dayjs from "@/utils/dayjs";
import type {Dayjs} from "dayjs";
import Translator from "@/utils/common/Translator.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import {NamaNavigationArgs} from "@/utils/common/NamaVueWidget.ts";
import DateRangePreset from "@/utils/constants/DateRangePreset.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";

interface DateRangeValue {
  from: string;
  to: string;
}

type PresetGroup = "current" | "previous" | "rolling";

interface Preset {
  key: string;
  group: PresetGroup;
  build: () => DateRangeValue;
}

const props = defineProps<WidgetProps>();
const emit = defineEmits<{ (e: "navigateToNext", args: NamaNavigationArgs): void }>();

const fieldId = props.field?.id;
const fieldUtil = new FieldWidgetUtil(props.presenter, props.fieldMetaData, true);

const RANGE_FORMAT = "YYYY-MM-DD";
const fmt = (d: Dayjs) => d.format(RANGE_FORMAT);

const presets: Preset[] =
  [
  {key: DateRangePreset.Today, group: "current", build: () => ({from: fmt(dayjs()), to: fmt(dayjs())})},
  {key: DateRangePreset.ThisWeek, group: "current", build: () => ({from: fmt(dayjs().startOf("week")), to: fmt(dayjs().endOf("week"))})},
  {
    key: DateRangePreset.ThisMonth,
    group: "current",
    build: () => ({from: fmt(dayjs().startOf("month")), to: fmt(dayjs().endOf("month"))})
  },
  {
    key: DateRangePreset.ThisQuarter, group: "current", build: () => {
      const qStart = dayjs().month() - (dayjs().month() % 3);
      return {from: fmt(dayjs().month(qStart).startOf("month")), to: fmt(dayjs().month(qStart + 2).endOf("month"))};
    }
  },
  {key: DateRangePreset.ThisYear, group: "current", build: () => ({from: fmt(dayjs().startOf("year")), to: fmt(dayjs().endOf("year"))})},
  {
    key: DateRangePreset.Yesterday,
    group: "previous",
    build: () => ({from: fmt(dayjs().subtract(1, "day")), to: fmt(dayjs().subtract(1, "day"))})
  },
  {
    key: DateRangePreset.PreviousWeek,
    group: "previous",
    build: () => ({from: fmt(dayjs().subtract(1, "week").startOf("week")), to: fmt(dayjs().subtract(1, "week").endOf("week"))})
  },
  {
    key: DateRangePreset.PreviousMonth,
    group: "previous",
    build: () => ({from: fmt(dayjs().subtract(1, "month").startOf("month")), to: fmt(dayjs().subtract(1, "month").endOf("month"))})
  },
  {
    key: DateRangePreset.PreviousQuarter, group: "previous", build: () => {
      const qStart = dayjs().month() - (dayjs().month() % 3) - 3;
      return {from: fmt(dayjs().month(qStart).startOf("month")), to: fmt(dayjs().month(qStart + 2).endOf("month"))};
    }
  },
  {
    key: DateRangePreset.PreviousYear,
    group: "previous",
    build: () => ({from: fmt(dayjs().subtract(1, "year").startOf("year")), to: fmt(dayjs().subtract(1, "year").endOf("year"))})
  },
  {key: DateRangePreset.Last7, group: "rolling", build: () => ({from: fmt(dayjs().subtract(6, "day")), to: fmt(dayjs())})},
  {key: DateRangePreset.Last30, group: "rolling", build: () => ({from: fmt(dayjs().subtract(29, "day")), to: fmt(dayjs())})},
  {key: DateRangePreset.Last90, group: "rolling", build: () => ({from: fmt(dayjs().subtract(89, "day")), to: fmt(dayjs())})},
  {key: DateRangePreset.Last365, group: "rolling", build: () => ({from: fmt(dayjs().subtract(364, "day")), to: fmt(dayjs())})},
];

const groupedPresets = computed(() => ({
  current: presets.filter(p => p.group === "current"),
  previous: presets.filter(p => p.group === "previous"),
  rolling: presets.filter(p => p.group === "rolling"),
}));

const popoverOpen = ref(false);
const triggerBtn = ref<HTMLButtonElement>();

const RANGE_SEPARATOR = "@";

interface ParsedFieldValue {
  range: DateRangeValue | null;
  presetKey: string | null;
}

function parseFieldValue(): ParsedFieldValue {
  let v = fieldUtil.fetchFieldValue();
  if (ObjectChecker.isEmptyOrNull(v))
    return {range: null, presetKey: null};
  const str = String(v);
  const preset = presets.find(p => p.key === str);
  if (preset) return {range: preset.build(), presetKey: str};
  if (str.includes(RANGE_SEPARATOR)) {
    const [from, to] = str.split(RANGE_SEPARATOR);
    return {range: {from: from ?? "", to: to ?? from ?? ""}, presetKey: null};
  }
  return {range: null, presetKey: null};
}

const initial = parseFieldValue();
const internalRange = ref<DateRangeValue | null>(initial.range);
const selectedPresetKey = ref<string | null>(initial.presetKey);
const customMode = ref(initial.presetKey === null && initial.range !== null);
const defaultYearMonth = ref(dayjs(initial.range?.from || undefined).format("YYYY/MM"));

watch(() => fieldUtil.fetchFieldValue(), () => {
  const next = parseFieldValue();
  internalRange.value = next.range;
  selectedPresetKey.value = next.presetKey;
  customMode.value = next.presetKey === null && next.range !== null;
  if (next.range?.from) defaultYearMonth.value = dayjs(next.range.from).format("YYYY/MM");
}, {deep: true});

const placeholderLabel = computed(() => props.customPlaceHolder || Translator.translate("selectRange"));

const triggerLabel = computed(() => {
  if (selectedPresetKey.value) {
    const preset = presets.find(p => p.key === selectedPresetKey.value);
    if (preset) return Translator.translate(preset.key);
  }
  if (ObjectChecker.isNotEmptyOrNull(internalRange.value?.from) && ObjectChecker.isNotEmptyOrNull(internalRange.value?.to)) {
    let format = 'D MMMM YYYY';
    return DateFieldUtils.formatDisplay(internalRange.value!.from, format) + " – " + DateFieldUtils.formatDisplay(internalRange.value!.to, format);
  }
  return placeholderLabel.value;
});

const hasSelection = computed(() =>
  ObjectChecker.isNotEmptyOrNull(internalRange.value?.from) && ObjectChecker.isNotEmptyOrNull(internalRange.value?.to)
);

function toQDate(s: string | undefined | null): string | null {
  if (!s) return null;
  const parsed = dayjs(s);
  return parsed.isValid() ? fmt(parsed) : null;
}

function fromQDate(s: string): string {
  const parsed = dayjs(s, RANGE_FORMAT, true);
  return parsed.isValid() ? fmt(parsed) : s;
}

const qDateModel = computed<{ from: string; to: string } | null>({
  get: () => {
    if (!internalRange.value) return null;
    const from = toQDate(internalRange.value.from);
    const to = toQDate(internalRange.value.to);
    if (!from && !to) return null;
    return {from: from || to!, to: to || from!};
  },
  set: () => {
  }
});

function onQDateUpdate(val: string | { from: string; to: string } | null) {
  if (!val) {
    void commitCustom(null);
    return;
  }
  if (typeof val === "string") {
    const single = fromQDate(val);
    void commitCustom({from: single, to: single});
    return;
  }
  void commitCustom({from: fromQDate(val.from), to: fromQDate(val.to)});
}

const fromInput = computed({
  get: () => internalRange.value?.from ?? "",
  set: (v: string) => onTextInputChange("from", v),
});

const toInput = computed({
  get: () => internalRange.value?.to ?? "",
  set: (v: string) => onTextInputChange("to", v),
});

function onTextInputChange(side: "from" | "to", v: string) {
  const parsed = dayjs(v, RANGE_FORMAT, true);
  if (!parsed.isValid()) return;
  const next: DateRangeValue = {
    from: side === "from" ? fmt(parsed) : (internalRange.value?.from ?? fmt(parsed)),
    to: side === "to" ? fmt(parsed) : (internalRange.value?.to ?? fmt(parsed)),
  };
  if (next.from && next.to && next.from > next.to)
    [next.from, next.to] = [next.to, next.from];
  void commitCustom(next);
}

function selectPreset(preset: Preset) {
  void commitPreset(preset);
}

function toggleCustom() {
  customMode.value = true;
  selectedPresetKey.value = null;
}

async function commitPreset(preset: Preset) {
  const r = preset.build();
  internalRange.value = r;
  selectedPresetKey.value = preset.key;
  customMode.value = false;
  defaultYearMonth.value = dayjs(r.from).format("YYYY/MM");
  fieldUtil.onPartialUserInput(preset.key);
  await fieldUtil.onBlur();
}

async function commitCustom(r: DateRangeValue | null) {
  internalRange.value = r;
  selectedPresetKey.value = null;
  customMode.value = ObjectChecker.isNotEmptyOrNull(r);
  let value: string = '';
  if (ObjectChecker.isNotEmptyOrNull(r))
    value = r?.from.toString() + RANGE_SEPARATOR + r?.to.toString();
  fieldUtil.onPartialUserInput(value);
  await fieldUtil.onBlur();
}

function clearSelection() {
  void commitCustom(null);
}

function closePopover() {
  popoverOpen.value = false;
}

function onTriggerClick() {
  if (fieldUtil.isFieldDisabled(props.forceEnableField)) return;
  popoverOpen.value = !popoverOpen.value;
}

async function onTriggerBlur() {
  if (popoverOpen.value)
    return;
  await fieldUtil.onBlur();
}

async function onPopoverHide() {
  await fieldUtil.onBlur();
}

function focus() {
  triggerBtn.value?.focus();
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function onTriggerKeyDown(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  if (popoverOpen.value || doNotGoToNextWithEnter) return;
  emit("navigateToNext", {keyboardEvent: e});
}

defineExpose({focus, canReceiveFocus});
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="FieldWidgetUtil.isSpanned(field)"
    :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()"
    :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget="{ doNotGoToNextWithEnter }">
      <div class="drw-root tw-inline-block tw-relative tw-w-full">
        <button
          ref="triggerBtn"
          type="button"
          :id="fieldId"
          :disabled="fieldUtil.isFieldDisabled(forceEnableField)"
          @click="onTriggerClick"
          @focus="fieldUtil.onFocus"
          @blur="onTriggerBlur"
          @keydown.enter="(e) => onTriggerKeyDown(e, doNotGoToNextWithEnter)"
          class="drw-trigger tw-group tw-flex tw-items-center tw-gap-2 tw-w-full tw-min-w-[200px] tw-px-2.5 tw-py-1 tw-rounded-md tw-border tw-text-xs tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/20"
          :class="popoverOpen
            ? 'tw-border-primary tw-bg-white dark:tw-bg-namaBackground-bright-dark'
            : 'tw-border-slate-200 dark:tw-border-slate-700 tw-bg-white dark:tw-bg-namaBackground-bright-dark hover:tw-border-slate-300'">
          <NamaIcon default-icon="nm-calendar" class="tw-size-3.5 active-primary"/>
          <span class="tw-flex-1 tw-min-w-0 tw-text-start tw-truncate tw-text-slate-700 dark:tw-text-slate-100">{{ triggerLabel }}</span>
          <NamaIcon default-icon="nm-arrow" class="tw-size-2.5 tw-text-slate-400 tw-transition-transform" :class="{'tw-rotate-180': popoverOpen}"/>

          <q-menu
            v-model="popoverOpen"
            @hide="onPopoverHide"
            anchor="bottom start"
            self="top start"
            no-parent-event
            no-focus
            no-refocus
            :offset="[0, 6]"
            class="!tw-rounded-xl !tw-bg-transparent">
            <div
              class="drw-panel tw-flex tw-flex-col tw-bg-white dark:tw-bg-namaBackground-bright-dark tw-rounded-xl tw-overflow-hidden tw-border tw-border-slate-200 dark:tw-border-slate-700"
              style="width: 540px; box-shadow: 0 12px 32px -10px rgba(15, 23, 42, 0.18), 0 4px 10px -4px rgba(15, 23, 42, 0.06);">

              <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-slate-100 dark:tw-border-slate-700/60">
                <input
                  type="text"
                  v-model="fromInput"
                  :placeholder="Translator.translate('from')"
                  class="tw-w-0 tw-flex-1 tw-min-w-0 tw-px-2 tw-py-1 tw-text-xs tw-text-slate-700 dark:tw-text-white tw-rounded-md tw-border tw-border-slate-200 dark:tw-border-slate-600 dark:tw-bg-namaBackground-dim-dark tw-bg-white focus:tw-outline-none focus:tw-border-primary tw-transition"/>
                <span class="tw-text-slate-300 tw-text-xs tw-inline-block" :class="{'tw-rotate-180': Translator.isLanguageRTL()}">→</span>
                <input
                  type="text"
                  v-model="toInput"
                  :placeholder="Translator.translate('to')"
                  class="tw-w-0 tw-flex-1 tw-min-w-0 tw-px-2 tw-py-1 tw-text-xs tw-text-slate-700 dark:tw-text-white tw-rounded-md tw-border tw-border-slate-200 dark:tw-border-slate-600 dark:tw-bg-namaBackground-dim-dark tw-bg-white focus:tw-outline-none focus:tw-border-primary tw-transition"/>
                <button
                  v-if="hasSelection"
                  type="button"
                  @click="clearSelection"
                  :title="Translator.translate('clear')"
                  class="tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-h-6 tw-px-2 tw-rounded-md tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400 hover:tw-text-primary hover:tw-bg-primary-light/10 dark:hover:tw-bg-primary-dark/15 tw-transition">
                  {{ Translator.translate('clear') }}
                </button>
                <button
                  type="button"
                  @click="closePopover"
                  :title="Translator.translate('close')"
                  class="tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-md tw-text-slate-400 hover:tw-text-slate-700 dark:hover:tw-text-white hover:tw-bg-slate-100 dark:hover:tw-bg-namaBackground-dim-dark tw-transition">
                  <NamaIcon default-icon="nm-cancel" class="tw-size-3"/>
                </button>
              </div>

              <div class="tw-flex" style="height: 280px;">
                <div class="tw-w-36 tw-shrink-0 tw-border-e tw-border-slate-100 dark:tw-border-slate-700/60 tw-flex tw-flex-col tw-min-h-0">
                  <div class="drw-presets tw-flex-1 tw-overflow-y-auto tw-py-1.5 tw-px-1.5 tw-space-y-2 tw-min-h-0">
                    <div v-for="(group, groupKey) in groupedPresets" :key="groupKey">
                      <div
                        class="tw-px-2 tw-pb-0.5 tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-400 dark:tw-text-slate-500">
                        {{ Translator.translate(groupKey) }}
                      </div>
                      <button
                        v-for="p in group"
                        :key="p.key"
                        type="button"
                        @click="selectPreset(p)"
                        class="tw-w-full tw-text-start tw-px-2 tw-py-1 tw-text-xs tw-rounded tw-transition-colors"
                        :class="selectedPresetKey === p.key
                          ? 'tw-bg-primary tw-text-white tw-font-medium'
                          : 'tw-text-slate-600 dark:tw-text-slate-300 hover:tw-bg-slate-100 dark:hover:tw-bg-namaBackground-dim-dark hover:tw-text-slate-900 dark:hover:tw-text-white'">
                        {{ Translator.translate(p.key) }}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    @click="toggleCustom"
                    class="tw-shrink-0 tw-mx-1.5 tw-mb-1.5 tw-mt-1 tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-transition-colors"
                    :class="customMode
                      ? 'tw-bg-primary tw-text-white'
                      : 'tw-border tw-border-dashed tw-border-slate-300 dark:tw-border-slate-600 tw-text-primary hover:tw-bg-primary-light/10 dark:hover:tw-bg-primary-dark/15'">
                    <NamaIcon default-icon="nm-calendar" class="tw-size-3"/>
                    {{ Translator.translate('customDate') }}
                  </button>
                </div>

                <div class="tw-flex-1 tw-flex tw-justify-center tw-items-start tw-p-2 tw-overflow-hidden">
                  <q-date
                    :model-value="qDateModel"
                    @update:model-value="onQDateUpdate"
                    range
                    no-unset
                    flat
                    :mask="RANGE_FORMAT"
                    :default-year-month="defaultYearMonth"
                    first-day-of-week="0"
                    class="nama-date drw-qdate"
                    color="primary"/>
                </div>
              </div>
            </div>
          </q-menu>
        </button>
      </div>
    </template>
  </WidgetContainer>
</template>

<style scoped>
.drw-root,
.drw-panel {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

.drw-trigger {
  letter-spacing: -0.005em;
}

.drw-presets {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.55) transparent;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.drw-presets::-webkit-scrollbar {
  width: 6px;
}

.drw-presets::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.4);
  border-radius: 3px;
  transition: background-color 0.15s ease;
}

.drw-presets:hover::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.65);
}

.drw-presets::-webkit-scrollbar-thumb:hover {
  background-color: rgba(148, 163, 184, 0.85);
}

.drw-presets::-webkit-scrollbar-track {
  background: transparent;
}

.drw-qdate {
  background: transparent;
  width: 100%;
  max-width: 320px;
  min-height: 0;
}

.drw-qdate :deep(.q-date__header) {
  display: none;
}

.drw-qdate :deep(.q-date__view) {
  padding: 4px;
  min-height: 0;
}

.drw-qdate :deep(.q-date__navigation) {
  height: 28px;
  padding: 0;
  margin-bottom: 4px;
}

.drw-qdate :deep(.q-date__navigation .q-btn) {
  font-size: 0.75rem;
  font-weight: 600;
}

.drw-qdate :deep(.q-date__calendar-weekdays > div) {
  font-size: 0.65rem;
  font-weight: 600;
  color: rgb(148, 163, 184);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 0;
}

.drw-qdate :deep(.q-date__calendar-item) {
  font-size: 0.7rem;
  padding: 1px;
}

.drw-qdate :deep(.q-date__calendar-item .q-btn) {
  min-height: 26px;
  min-width: 26px;
  font-size: 0.7rem;
}

.drw-qdate :deep(.q-date__range),
.drw-qdate :deep(.q-date__range-from),
.drw-qdate :deep(.q-date__range-to) {
  background-color: rgba(var(--nama-primary-light), 0.10) !important;
}

.tw-dark .drw-qdate :deep(.q-date__range),
.tw-dark .drw-qdate :deep(.q-date__range-from),
.tw-dark .drw-qdate :deep(.q-date__range-to) {
  background-color: rgba(var(--nama-primary-dark), 0.18) !important;
}

.drw-qdate :deep(.q-date__range-from)::before {
  border-top-left-radius: 9999px;
  border-bottom-left-radius: 9999px;
}

.drw-qdate :deep(.q-date__range-to)::before {
  border-top-right-radius: 9999px;
  border-bottom-right-radius: 9999px;
}

.drw-qdate :deep(.q-date__calendar-item--in .q-btn--standard.bg-primary) {
  background: var(--nama-primary) !important;
}

.drw-qdate :deep(.q-date__today) {
  box-shadow: inset 0 0 0 1px rgba(var(--nama-primary-light), 0.4);
}

.tw-dark .drw-qdate :deep(.q-date__today) {
  box-shadow: inset 0 0 0 1px rgba(var(--nama-primary-dark), 0.5);
}
</style>
